import React, { useState, useMemo } from 'react';

const SVG_W = 800;
const SVG_H = 800;
const M = { top: 48, right: 48, bottom: 48, left: 48 };
const PW = SVG_W - M.left - M.right;
const PH = SVG_H - M.top  - M.bottom;

const SESSION_COLORS = [
  '#3B82F6','#10B981','#F59E0B','#8B5CF6',
  '#EC4899','#06B6D4','#84CC16','#F97316',
  '#14B8A6','#6366F1','#EF4444','#A855F7',
];

const ExperienceMatrixAnalysis = ({ sessions, cards, config }) => {
  const [hovered, setHovered] = useState(null); // { cardId, sessionId, label, svgX, svgY }
  const [focusedCard, setFocusedCard] = useState(null); // card id to highlight
  const [viewMode, setViewMode] = useState('combined'); // 'combined' | 'individual'
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  const toSvgX = (x) => M.left + x * PW;
  const toSvgY = (y) => M.top  + y * PH;

  // Keep each session's stable colour index, then narrow to the current view.
  const indexedSessions = useMemo(
    () => sessions.map((s, i) => ({ ...s, colorIndex: i })),
    [sessions]
  );
  const viewSessions = useMemo(() => (
    viewMode === 'individual' && selectedSessionId
      ? indexedSessions.filter(s => s.id === selectedSessionId)
      : indexedSessions
  ), [indexedSessions, viewMode, selectedSessionId]);

  // Sessions that actually placed something — for the individual-view picker.
  const sessionsWithData = useMemo(
    () => indexedSessions.filter(s => (s.matrixPlacements || []).length > 0),
    [indexedSessions]
  );

  // For each card, collect placements across the sessions in view
  const cardData = useMemo(() => {
    return cards.map((card, cardIdx) => {
      const placements = viewSessions.flatMap((session) => {
        const p = session.matrixPlacements?.find(mp => mp.touchpoint_card_id === card.id);
        if (!p) return [];
        return [{
          sessionId: session.id,
          sessionIdx: session.colorIndex,
          x: toSvgX(p.x_value),
          y: toSvgY(p.y_value),
          rawX: p.x_value,
          rawY: p.y_value,
        }];
      });

      // Centroid for label placement
      const centroid = placements.length > 0
        ? {
            x: placements.reduce((s, p) => s + p.x, 0) / placements.length,
            y: placements.reduce((s, p) => s + p.y, 0) / placements.length,
          }
        : null;

      return { card, cardIdx, placements, centroid };
    }).filter(d => d.placements.length > 0);
  }, [viewSessions, cards]);

  // Custom card placements (participant-created)
  const customPlacements = useMemo(() => {
    return viewSessions.flatMap((session) =>
      (session.matrixPlacements || [])
        .filter(p => p.is_custom)
        .map(p => ({
          sessionId: session.id,
          sessionIdx: session.colorIndex,
          label: p.label || p.custom_label || 'Custom',
          x: toSvgX(p.x_value),
          y: toSvgY(p.y_value),
        }))
    );
  }, [viewSessions]);

  const hasData = cardData.length > 0 || customPlacements.length > 0;

  // Legend items — unique sessions that placed at least one card
  const activeSessions = useMemo(() => {
    const ids = new Set([
      ...cardData.flatMap(d => d.placements.map(p => p.sessionId)),
      ...customPlacements.map(p => p.sessionId),
    ]);
    return sessions
      .map((s, i) => ({ ...s, colorIndex: i }))
      .filter(s => ids.has(s.id));
  }, [sessions, cardData, customPlacements]);

  return (
    <div className="flex flex-col h-full">

      {/* ── Controls bar ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-4 px-4 py-3 bg-white border-b border-gray-100 shrink-0 flex-wrap">

        {/* View mode toggle */}
        <div className="flex rounded-md overflow-hidden border border-gray-200">
          {['combined', 'individual'].map(mode => (
            <button key={mode} onClick={() => setViewMode(mode)}
              className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors
                ${viewMode === mode ? 'bg-rutabaga-dark text-white' : 'bg-white text-gray-400 hover:bg-gray-50'}`}>
              {mode}
            </button>
          ))}
        </div>

        {viewMode === 'individual' && (
          <select value={selectedSessionId || ''} onChange={e => setSelectedSessionId(e.target.value || null)}
            className="text-[10px] font-bold border border-gray-200 rounded px-2 py-1.5 text-gray-600 outline-none focus:border-rutabaga-pink">
            <option value="">Select session…</option>
            {sessionsWithData.map(s => {
              const n = (s.matrixPlacements || []).length;
              return (
                <option key={s.id} value={s.id}>
                  Session {s.colorIndex + 1} — {n} card{n !== 1 ? 's' : ''}
                </option>
              );
            })}
          </select>
        )}

        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
          {activeSessions.length} session{activeSessions.length !== 1 ? 's' : ''} ·{' '}
          {cardData.length} card{cardData.length !== 1 ? 's' : ''} placed
        </p>

        {/* Card filter pills */}
        <div className="flex gap-1.5 flex-wrap">
          {cards.map(card => {
            const hasData = cardData.some(d => d.card.id === card.id);
            if (!hasData) return null;
            const isFocused = focusedCard === card.id;
            return (
              <button
                key={card.id}
                onClick={() => setFocusedCard(isFocused ? null : card.id)}
                className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wide transition-colors
                  ${isFocused
                    ? 'bg-rutabaga-dark text-white'
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
              >
                {card.content}
              </button>
            );
          })}
        </div>

        {/* Session color legend */}
        <div className="ml-auto flex items-center gap-3">
          {activeSessions.slice(0, 8).map((s, i) => (
            <div key={s.id} className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: SESSION_COLORS[s.colorIndex % SESSION_COLORS.length] }} />
              <span className="text-[9px] text-gray-400 font-bold">{i + 1}</span>
            </div>
          ))}
          {activeSessions.length > 8 && (
            <span className="text-[9px] text-gray-400 font-bold">+{activeSessions.length - 8}</span>
          )}
        </div>
      </div>

      {/* ── Canvas ────────────────────────────────────────────────────────── */}
      {!hasData ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="font-black text-rutabaga-dark">No touchpoint mapping data yet</p>
            <p className="text-gray-400 text-sm mt-1">Complete sessions with the touchpoint mapping module to see results here.</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto bg-rutabaga-bg flex items-center justify-center p-8">
          <div className="relative bg-white rounded-lg border border-gray-200 shadow-sm" style={{ width: '100%', maxWidth: 640, aspectRatio: '1/1' }}>

            {/* Axis labels */}
            <div className="absolute -top-6 inset-x-0 text-center">
              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{config?.y_top || 'Top'}</span>
            </div>
            <div className="absolute -bottom-6 inset-x-0 text-center">
              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{config?.y_bottom || 'Bottom'}</span>
            </div>
            <div className="absolute -left-12 inset-y-0 flex items-center">
              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest -rotate-90 whitespace-nowrap">{config?.x_left || 'Left'}</span>
            </div>
            <div className="absolute -right-12 inset-y-0 flex items-center">
              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest rotate-90 whitespace-nowrap">{config?.x_right || 'Right'}</span>
            </div>

            <svg
              viewBox={`0 0 ${SVG_W} ${SVG_H}`}
              width="100%" height="100%"
              style={{ display: 'block', overflow: 'visible' }}
            >
              {/* Axis lines */}
              <line x1={SVG_W / 2} y1={M.top} x2={SVG_W / 2} y2={SVG_H - M.bottom}
                stroke="#F3F4F6" strokeWidth={1} />
              <line x1={M.left} y1={SVG_H / 2} x2={SVG_W - M.right} y2={SVG_H / 2}
                stroke="#F3F4F6" strokeWidth={1} />

              {/* ── Per-card placement dots ────────────────────────────── */}
              {cardData.map(({ card, placements, centroid }) => {
                const isFocused = focusedCard === card.id;
                const isDimmed = focusedCard && !isFocused;

                return (
                  <g key={card.id} opacity={isDimmed ? 0.15 : 1}>
                    {/* Dots for each session's placement */}
                    {placements.map(p => (
                      <circle
                        key={`${card.id}-${p.sessionId}`}
                        cx={p.x} cy={p.y}
                        r={hovered?.cardId === card.id && hovered?.sessionId === p.sessionId ? 8 : 6}
                        fill={SESSION_COLORS[p.sessionIdx % SESSION_COLORS.length]}
                        stroke="white" strokeWidth={2}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => setHovered({ cardId: card.id, sessionId: p.sessionId, label: card.content, svgX: p.x, svgY: p.y })}
                        onMouseLeave={() => setHovered(null)}
                      />
                    ))}

                    {/* Card label at centroid */}
                    {centroid && (
                      <text
                        x={centroid.x}
                        y={centroid.y - 12}
                        textAnchor="middle"
                        fontSize={9}
                        fontFamily="Roboto,sans-serif"
                        fontWeight="700"
                        fill={isFocused ? '#1F2937' : '#6B7280'}
                        letterSpacing="0.03em"
                        style={{ pointerEvents: 'none' }}
                      >
                        {card.content}
                      </text>
                    )}

                    {/* Convex hull / spread indicator — simple circle around cluster */}
                    {placements.length > 1 && centroid && (() => {
                      const maxR = Math.max(...placements.map(p =>
                        Math.sqrt((p.x - centroid.x) ** 2 + (p.y - centroid.y) ** 2)
                      ));
                      return (
                        <circle
                          cx={centroid.x} cy={centroid.y}
                          r={maxR + 8}
                          fill="none"
                          stroke={isFocused ? '#E02F50' : '#E5E7EB'}
                          strokeWidth={isFocused ? 1.5 : 1}
                          strokeDasharray="3,3"
                          style={{ pointerEvents: 'none' }}
                        />
                      );
                    })()}
                  </g>
                );
              })}

              {/* ── Custom card placements ─────────────────────────────── */}
              {customPlacements.map((p, i) => (
                <g key={`custom-${i}`}>
                  <circle
                    cx={p.x} cy={p.y}
                    r={5}
                    fill={SESSION_COLORS[p.sessionIdx % SESSION_COLORS.length]}
                    stroke="white" strokeWidth={2}
                    strokeDasharray="2,1"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHovered({ label: `${p.label} (custom)`, svgX: p.x, svgY: p.y })}
                    onMouseLeave={() => setHovered(null)}
                  />
                </g>
              ))}
            </svg>

            {/* Hover tooltip */}
            {hovered && (
              <div
                className="absolute pointer-events-none z-10 bg-rutabaga-dark text-white text-[10px] font-bold px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap"
                style={{
                  left: `${(hovered.svgX / SVG_W) * 100}%`,
                  top: `${(hovered.svgY / SVG_H) * 100}%`,
                  transform: 'translate(-50%, -140%)',
                }}
              >
                {hovered.label}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperienceMatrixAnalysis;
